/* @flow */
/* eslint-env es6,browser */

import { logError, logDebug } from '../utils/logger';
import {
  extend,
  noop,
  isObject,
  isString,
  isArray,
  isFunction
} from '../utils/coreutils';
import {
  isBrowserExcluded,
  getBrowserExcludeList,
  loadJS,
  loadJSWithTimeout
} from '../utils/browserutil';
import { createIframe } from '../utils/iframeutils';
import { getCCPA } from '../utils/ccpautils';
import { getUserID } from '../idproviders/idputils';
import {
  PREBID_UID_PUBCOMMON,
  PREBID_UID_LIVERAMP,
  PREBID_UID_VERIZON,
  PREBID_UID_CRITEO,
  PREBID_UID_FABRICK,
  PREBID_UID_UNIFIEDID
} from '../idproviders/idpconstants';

/**
 * Enum for initialization state
 *
 * @readonly
 * @global
 * @enum {number}
 */
export const INIT_STATUS = {
  /** Not initialized */
  F_NOT_INITED: 0,
  /** Initialized */
  F_INITED: 1,
  /** Failed to initialize */
  F_FAILED: -1,
  /** Init timed out */
  F_TIMEOUT: -2
};

/**
 * @global
 * @typedef hbConfig
 * @property {Array<string>} positions List of positions
 * @property {object} posTokenMap Position token map
 * @property {string} cVar custom Vars
 * @property {string} [exclude]  Excluded list of positions
 * @property {number} [timeout=2000] Timeout
 * @property {string} [script=''] Script to execute
 * @property {string} [initTimeout=1000] HB Partner initialization timeout
 * @property {string} [bidUrl=''] Bid URL
 * @property {string} [beaconUrl=''] Beacon URL
 */
/**
 * @typedef hbPartnerData
 */

/**
 * @global
 * @typedef hbPosData
 * @property {string} info Position Info
 * @property {object} _QP Position Query Param
 * @property {object} _CV Position Custom Vars
 * @property {hb_data} winningBid Winning bid
 */

/**
 * @global
 * @typedef hb_data
 * @property {string} hb_pb Bid value
 * @property {string} hb_bidder Bidder name
 * @property {string} hb_adid Bid ID/creative
 */

/**
 * @typedef hbBidCacheBase
 * @property {string} adjustedPrice adjusted price
 * @property {string} cVar custom var
 * @property {string} ad Ad content
 * @property {string} nurl Ad URL
 */

/**
 * @description Provides interface for all bidders and provide common functionality
 * @module _bidbase
 * @requires module:utils/logger
 * @requires module:utils/coreutils
 * @requires module:utils/Browserutil
 * @requires module:utils/iframeutils
 * @requires module:commonid
 * @property {string} _name Name
 * @property {hbConfig} _config Config
 * @property {number} [_bInited=0] init status; 0 - NOT_INITED, 1 - INITED, -1 - FAILED
 * @property {Array<string>} _excludeList browser exclude list
 * @property {Array<string>} _mandatoryConfigs list of mandatory configs to be checked in init
 * @property {string} [_posId='id'] position specific key in posTokenMap
 * @property {string} [_isSupported=true] whether partner is supported or not. populated by partner constructor
 * @property {object} _bidReponses response object used by renderAd for rendering
 * @property {string} [_renderType='html'] rendering type; html - doc write ad html, iframe - load given url in iframe, func - call given func with id
 * @property {Function} _renderFunc Render function
 */

class _bidbase {
  _config: hbPartnerConfigVariant;

  _name: string;

  _bInited: initStatus;

  _initCheck: initChecker;

  _DefConfig: hbPartnerConfigVariant;

  // browser exclude list support
  _excludeList: Array<string>;

  _mandatoryConfigs: Array<string>;

  _posId: string;

  _bidResponses: { [key: string]: any }; // flowlint-line unclear-type:off

  _renderType: string;

  _renderFunc: cbFunc;

  _isSupported: boolean;

  _bidPriceDiscount: number;

  _enableReporting: boolean;

  _beaconUrlId: string;

  _cachedBids: hbBidCacheStore;

  constructor(name: string) {
    this._name = name;
    this._bInited = INIT_STATUS.F_NOT_INITED;
    this._initCheck = {
      context: {},
      obj: '',
      func: ''
    };
    this._DefConfig = {
      positions: [],
      exclude: '',
      cVar: '',
      timeout: 2000,
      posTokenMap: {},
      script: '',
      initTimeout: 1000,
      bidUrl: '',
      beaconUrl: '',
      enableUserSync: false, // bidder level config to disable user sync
      syncUrl: '',
      useBidCache: false, // disabled by default
      ttl: 300, // bid caching for 5 min
      enableCommonId: false
    };
    this._config = extend(true, {}, this._DefConfig);
    // browser exclude list support
    this._excludeList = [];
    this._mandatoryConfigs = [];
    this._posId = 'id';
    this._bidResponses = {};
    this._renderType = 'html';
    this._renderFunc = noop;
    this._isSupported = true;
    this._bidPriceDiscount = 1;
    this._enableReporting = true;
    this._beaconUrlId = '';
    this._cachedBids = {};
  }

  /**
   * getter for name
   *
   * @returns {string} - Partner name
   */
  getName() {
    return this._name;
  }

  /**
   * get list of bid positions supported by this header bidder
   *
   * @param {string} pName - page name
   * @returns {Array<string>} - Array of ad positions
   */
  getBidPositions(pName: string): Array<string> {
    return !this._isSupported || isBrowserExcluded(pName, this._excludeList)
      ? []
      : this._config.positions;
  }

  /**
   * Reset & Update config specific to this bidder
   *
   * @param {hbConfig} oConfig - Partner config
   * @returns {undefined}
   */
  updateConfig(oConfig: hbPartnerConfig) {
    this._config = extend(true, {}, this._DefConfig, oConfig);
    this._bidPriceDiscount = (100 - (this._config.bidDiscount || 0)) / 100;
    // compute exclusion list again
    this._excludeList = getBrowserExcludeList(this._config.exclude);
    this._postUpdateConfig();
  }

  // eslint-disable-next-line no-empty-function
  _postUpdateConfig() {}

  /**
   * getter for qpkey
   *
   * @param {string} pos - Position name
   * @returns {string} - QP key
   */
  getQPKey(pos: string): string {
    return isObject(this._config.posTokenMap[pos]) &&
      this._config.posTokenMap[pos].qpKey
      ? this._config.posTokenMap[pos].qpKey
      : this._config.qpKey || '';
  }

  /**
   * getter for posKey
   *
   * @param {string} pos - Position name
   * @returns {string} - Position key
   */
  getPosKey(pos: string): string {
    return isObject(this._config.posTokenMap[pos])
      ? this._config.posTokenMap[pos][this._posId]
      : '';
  }

  /**
   * getter for lineId
   *
   * @param {string} pos - Position name
   * @returns {string} - Line ID
   */
  getLineId(pos: string): string {
    return isObject(this._config.posTokenMap[pos])
      ? this._config.posTokenMap[pos].lineId
      : '';
  }

  /**
   * getter for cVar
   *
   * @returns {string} - Custom Var
   */
  getCVar(): string {
    return this._config.cVar;
  }

  /**
   * Default implementation of preInit. If required bidder implementation should override this.
   *
   * @returns {INIT_STATUS} - Pre-init status
   * @access private
   */
  _preInit(): initStatus {
    return INIT_STATUS.F_INITED;
  }

  /**
   *
   * Default implementation for postInit. Bidders can override it
   *
   * @access private
   * @returns {INIT_STATUS}  - Post-init status
   * @memberof _bidbase
   */
  _postInit(): initStatus {
    return INIT_STATUS.F_INITED;
  }

  /**
   * checks for mandatory config
   *
   * @returns {INIT_STATUS} - Config check status
   * @access private
   */
  _configCheck(): initStatus {
    // mandatory config string check
    const invalidConfigs = this._mandatoryConfigs.reduce(
      (acc, conf) => acc.concat(!this._config[conf].length ? conf : []),
      []
    );
    if (invalidConfigs.length) {
      logError(
        `${this._name} config error: missing ${invalidConfigs.join(',')}`
      );
      return INIT_STATUS.F_FAILED;
    }

    // presence of posTokenMap object
    if (!isObject(this._config.posTokenMap)) {
      logError(`${this._name} config error: missing posTokenMap`);
      return INIT_STATUS.F_FAILED;
    }

    // all positions in positions array should be configured in posTokenMap
    let misPos = this._config.positions.filter(
      pos => !this._config.posTokenMap[pos]
    );
    if (misPos.length) {
      logError(
        `${
          this._name
        } config error: missing positions in posTokenMap ${misPos.join(',')}`
      );
      return INIT_STATUS.F_FAILED;
    }

    // all positions in positions array should have lineId in posTokenMap
    misPos = this._config.positions.filter(pos => !this.getLineId(pos));
    if (misPos.length) {
      logError(
        `${
          this._name
        } config error: missing lineId in posTokenMap ${misPos.join(',')}`
      );
      return INIT_STATUS.F_FAILED;
    }

    return INIT_STATUS.F_INITED;
  }

  /**
   * Default implementation of _externalScriptCheck. If required bidder implementation should override this.
   *
   * @returns {INIT_STATUS} - External script check
   * @access private
   */
  _externalScriptCheck(): initStatus {
    const { context, func = '', obj = '' } = this._initCheck;
    const cxtCheck = context === window || isObject(context);
    if (
      !cxtCheck ||
      (isString(obj) && obj.length > 0 && !isObject(context[obj])) ||
      (isString(func) && func.length > 0 && !isFunction(context[func]))
    ) {
      return INIT_STATUS.F_FAILED;
    }
    return INIT_STATUS.F_INITED;
  }

  /**
   * _init - init functionality
   * extended classes need to override _preInit if any additional functionality is required
   *
   * @returns {Promise} - Promise with init status
   * @access private
   */
  _init(): Promise<void | initStatus> {
    if (this._bInited === INIT_STATUS.F_TIMEOUT) {
      const extStatus = this._externalScriptCheck();
      if (extStatus === INIT_STATUS.F_FAILED) {
        logError(`${this._name} _externalScriptCheck failed`);
        this._bInited = extStatus;
        return Promise.reject();
      }
      if (extStatus === INIT_STATUS.F_INITED) {
        this._bInited = this._postInit();
      } else {
        this._bInited = extStatus;
      }
      return Promise.resolve();
    }

    if (this._bInited === INIT_STATUS.F_INITED) {
      return Promise.resolve();
    }

    if (this._bInited === INIT_STATUS.F_FAILED) {
      return Promise.reject();
    }

    if (this._configCheck() === INIT_STATUS.F_FAILED) {
      this._bInited = INIT_STATUS.F_FAILED;
      return Promise.reject();
    }

    if (this._preInit() === INIT_STATUS.F_FAILED) {
      logError(`${this._name} preInit failed`);
      this._bInited = INIT_STATUS.F_FAILED;
      return Promise.reject();
    }

    if (this._config.beaconUrl.length) {
      loadJS(this._config.beaconUrl, document, true, this._beaconUrlId);
    }

    let loadPromise = Promise.resolve();
    if (this._config.script.length) {
      loadPromise = new Promise((resolve, reject) => {
        loadJSWithTimeout(
          this._config.script,
          document,
          true,
          this._config.initTimeout
        ).then(
          () => {
            if (this._externalScriptCheck() === INIT_STATUS.F_INITED) {
              resolve();
            } else {
              // setting TIMEOUT to check externel script load during next init
              // though script is loaded, script execution is not guaranteed
              this._bInited = INIT_STATUS.F_TIMEOUT;
              logError(`${this._name} _externalScriptCheck failed`);
              reject();
            }
            return this._bInited;
          },
          () => {
            // script load failed, it could be due to timeout
            // setting TIMEOUT to check externel script load during next init
            this._bInited = INIT_STATUS.F_TIMEOUT;
            reject();
          }
        );
      });
    }

    return loadPromise.then(() => {
      this._bInited = this._postInit();
      logDebug(`${this._name} Init is done. getting ad bids`);
      return this._bInited;
    });
  }

  /**
   * get bids default implementation return zero bids.
   * Needs to override in bidders.
   *
   * @access private
   * @param {Array<string>} posList - HB positions list
   * @param {hbPartnerData} ret - Return data
   * @returns {Promise<hbPartnerData>} - Return promise
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return Promise.resolve(ret);
  }

  /**
   * This method should be overriden in bidders with enabled user sync
   *
   * @access private
   * @external syncObj
   * @returns {Array<syncObj>} - Array of sync objects
   */
  _getUserSync(): Array<syncObj> {
    return [];
  }

  /**
   * This method will be called for bidders witout overriden userSync method
   *
   * @returns {Array<syncObj>} - Array of sync objects
   */
  getUserSync(): Array<syncObj> {
    if (!this.isSyncUserEnabled()) {
      return [];
    }
    return this._getUserSync();
  }

  /**
   * Returns if user sync is enabled for the biddder
   *
   * @returns {boolean} - Sync enabled
   */
  isSyncUserEnabled(): boolean {
    return this._config.enableUserSync;
  }

  /**
   * init and get bids
   *
   * @param {Array<string>} posList - list of ad positions
   * @param {hbPartnerData} ret - header bidding default response
   * @returns {Promise<hbPartnerData>} - Promise
   */
  getBids(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    logDebug(`${this._name} getting bids for ${posList.join(',')}`);
    return this._init()
      .then(
        () => this._getAds(posList, ret),
        () => ret
      )
      .then(r => this._useCachedBid(r));
  }

  /**
   * pre function render ad for this partner
   *
   * @external posConfigInfo
   * @access private
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - Position DOM ID
   * @param {posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  _preRenderAd(
    win: window,
    doc: Document,
    pos: string,
    // eslint-disable-next-line no-unused-vars
    id: string,
    // eslint-disable-next-line no-unused-vars
    posInfo: posConfigInfo
  ) {
    // irrespective of whether we are able to successfully render or not,
    // clear the cached bid
    this._clearCachedBid(pos);
  }

  /**
   * render ad for this partner
   *
   * @access private
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - Position DOM ID
   * @param {posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  _renderAd(
    win: window,
    doc: Document,
    pos: string,
    id: string,
    posInfo: posConfigInfo
  ) {
    try {
      logDebug(`Rendering ad in position ${pos} for partner ${this._name}`);
      if (this._renderType === 'func') {
        this._renderFunc.call(window, doc, id);
      } else if (this._renderType === 'html') {
        const res = this._bidResponses[id];
        doc.write(res.ad);
        if (res.nurl && res.nurl.length) {
          doc.write(`<img src="${res.nurl}">`);
        }
      } else if (this._renderType === 'iframe') {
        const { width, height } = posInfo;
        const newIf = createIframe(
          doc,
          `${this._name}-${id}`,
          width,
          height,
          this._bidResponses[id].ad
        );
        if (doc.body) {
          doc.body.appendChild(newIf);
        }
      } else {
        logError(`${this._name} ${pos} render ad invalid render type`);
      }
    } catch (error) {
      logError(`${this._name} ${pos} render ad failure ${error.message}`);
    }
  }

  /**
   * render ad for this partner
   *
   * @param {window} win - window object
   * @param {Document} doc - document object
   * @param {string} pos - ad position
   * @param {string} id - Position DOM ID
   * @param {posConfigInfo} posInfo - Position information
   * @returns {undefined}
   */
  renderAd(
    win: window,
    doc: Document,
    pos: string,
    id: string,
    posInfo: posConfigInfo
  ) {
    this._preRenderAd(win, doc, pos, id, posInfo);
    this._renderAd(win, doc, pos, id, posInfo);
  }

  /**
   * Validates RTB response format
   *
   * @param {object} resp - RTB response object
   * @returns {boolean} - valid RTB
   * @access private
   */
  _isValidRTBResponse(resp: RTBBidResponse): boolean {
    if (
      isObject(resp) &&
      isArray(resp.seatbid) &&
      resp.seatbid.length > 0 &&
      isArray(resp.seatbid[0].bid) &&
      resp.seatbid[0].bid.length
    ) {
      return true;
    }
    return false;
  }

  _isBidCachingEnabled(): boolean {
    return this._config.useBidCache;
  }

  /**
   * Get RTB User Object with Eid array based on the keys passed
   *
   * @param {object} eidKeys - Array of User ID module keys to get values for
   * @returns {boolean} - Object with Array of Eid objects for keys requested
   * @access private
   */
  _getRTBUserEids(eidKeys: $ReadOnlyArray<string> = []): RTBUserIDs {
    /* User ID modules information */
    const EidsMap = {
      [PREBID_UID_LIVERAMP]: {
        fn: getUserID,
        args: [PREBID_UID_LIVERAMP],
        source: 'liveramp.com',
        atype: 3
      },
      // Verizon Media ConnectID
      [PREBID_UID_VERIZON]: {
        fn: getUserID,
        args: [PREBID_UID_VERIZON],
        source: 'verizonmedia.com',
        atype: 3
      },
      [PREBID_UID_PUBCOMMON]: {
        fn: getUserID,
        args: [PREBID_UID_PUBCOMMON],
        source: 'pubcid.org',
        atype: 1
      },
      [PREBID_UID_CRITEO]: {
        fn: getUserID,
        args: [PREBID_UID_CRITEO],
        source: 'criteo.com',
        atype: 1
      },
      [PREBID_UID_UNIFIEDID]: {
        fn: getUserID,
        args: [PREBID_UID_UNIFIEDID],
        source: 'adserver.org',
        atype: 1
      },
      [PREBID_UID_FABRICK]: {
        fn: getUserID,
        args: [PREBID_UID_FABRICK],
        source: 'neustar.biz',
        atype: 1
      }
    };

    // if no argument are passed, assume that partner needs all
    const reqEids = eidKeys.length > 0 ? eidKeys : Object.keys(EidsMap);

    /* Add eid data for the User IDs we have in the Eids map */
    const eids = reqEids
      .filter(eidKey => EidsMap[eidKey] && isFunction(EidsMap[eidKey].fn))
      .reduce((_eids, idKey) => {
        const { fn, args, source, atype } = EidsMap[idKey];
        const idVal = fn(...args);
        if (idVal.length > 0) {
          _eids.push({
            source,
            uids: [
              {
                id: idVal,
                atype
              }
            ]
          });
        }
        return _eids;
      }, []);
    if (eids.length === 0) {
      return {};
    }
    return {
      user: {
        eids
      }
    };
  }

  _clearCachedBid(pos: string = '') {
    if (
      pos.length > 0 &&
      this._isBidCachingEnabled() &&
      this._cachedBids[pos]
    ) {
      logDebug(`${this._name} clearing cached bid for ${pos}`);
      delete this._cachedBids[pos];
    }
  }

  _isCachedBidExpired(storedTS: number, tsNow: number): boolean {
    return tsNow - storedTS > this._config.ttl * 1000;
  }

  /**
   *
   * @private
   * @param {string} pos - Ad position
   * @param {hbBidCacheBase} param1 - Bid data
   * @returns {undefined} - Nothing to return here
   */
  _updateBidCache(
    pos: string = '',
    { adjustedPrice = '', cVar = '', ad = '', nurl = '' }: hbBidCacheBase = {}
  ) {
    const ts = Date.now();
    // check input param
    if (!pos.length || !adjustedPrice.length || !cVar.length) {
      return;
    }

    // check if caching is enabled
    if (!this._isBidCachingEnabled()) {
      return;
    }

    let bStore = false;
    if (!this._cachedBids[pos]) {
      // we don't have cached bids. Let's cache this one
      bStore = true;
    } else {
      // get stored bid details
      const o: hbBidCacheStoreData = this._cachedBids[pos];
      const { ts: storedTS = 0, adjustedPrice: storedPrice = '' } = o;
      const bExpired = this._isCachedBidExpired(storedTS, ts);
      const bHigherPrice = Number(storedPrice) > Number(adjustedPrice);
      if (!bExpired && bHigherPrice) {
        logDebug(
          `${this._name} ${pos} cached bid is untouched Expired:${
            bExpired ? 1 : 0
          } HigherPrice:${bHigherPrice ? 1 : 0} elapsed(s):${
            (ts - storedTS) / 1000
          } savedPrice:${storedPrice} newPrice:${adjustedPrice}`
        );
      } else {
        // old one is either expired or lower bid value
        // in both cases let's use the new bid and store the same
        bStore = true;
      }
    }

    if (bStore) {
      // store the bid values based on above condition
      logDebug(`${this._name} ${pos} caching bid ${adjustedPrice} ${cVar}`);
      this._cachedBids[pos] = {
        adjustedPrice,
        cVar,
        ad,
        nurl,
        ts
      };
    }
  }

  /**
   * @description Fill/replace cached bid data as perrules
   * @access private
   * @param {hbPartnerData} data - Response data
   * @returns {hbPartnerData} Updated response data
   */
  _useCachedBid(data: hbPartnerData): hbPartnerData {
    if (!this._isBidCachingEnabled()) {
      return data;
    }

    const ts = Date.now();
    Object.entries(data)
      .filter(([pos, d]) => {
        if (!this._cachedBids[pos]) {
          // we don't have stored bid for this position.
          // Nothing further to do here.
          return false;
        }
        // $FlowFixMe
        const p = Number(d.winningBid.hb_pb);
        const { adjustedPrice: sp, ts: storedTS } = this._cachedBids[pos];
        // do price comparision
        return Number(sp) > p && !this._isCachedBidExpired(storedTS, ts);
      })
      .forEach(([pos]) => {
        // cached bid seems to have higher price. Let's use it
        const { adjustedPrice, cVar, ad, nurl } = this._cachedBids[pos];
        this._updateResponseData(data, pos, adjustedPrice, cVar, ad, nurl);
        logDebug(
          `${this._name} ${pos} using Cached bid ${cVar} ${adjustedPrice}`
        );
      });

    return data;
  }

  _updateResponseData(
    ret: hbPartnerData,
    pos: string = '',
    price: string = '',
    cVar: string = '',
    ad: string = '',
    nurl: string = ''
  ) {
    if (!pos.length) {
      return;
    }
    // query param string to pass to OpenX ad call
    ret[pos].info = this._enableReporting
      ? `${this.getLineId(pos)}|${price}`
      : '';
    ret[pos]._QP[this.getQPKey(pos)] = price;
    ret[pos]._CV[this._config.cVar] = cVar;
    ret[pos].winningBid.hb_pb = price;
    ret[pos].winningBid.hb_adid = cVar;
    if (ad && ad.length) {
      this._bidResponses[cVar] = { ad, nurl };
    }
  }

  /**
   * Populates ret object with info, QP & CV
   * Stores response in bidResponses for use in renderAd later
   *
   * @param {hbPartnerData} ret - Partner Data
   * @param {string} pos - Position name
   * @param {string} price - Bid price
   * @param {string} cVar - custom var
   * @param {string} ad - Ad
   * @param {string} nurl - nurl
   * @returns {undefined}
   * @access private
   */
  _handleBidPosData(
    ret: hbPartnerData,
    pos: string,
    price: string,
    cVar: string,
    ad?: string = '',
    nurl?: string = ''
  ) {
    const adjustedPrice = String(this._bidPriceAdjust(price));

    logDebug(
      `${this._name} pos:${pos} _handleBidPosData price:${price} adjprice:${adjustedPrice}`
    );

    // update the response data
    this._updateResponseData(ret, pos, adjustedPrice, cVar, ad, nurl);
    // check and cache the bid
    this._updateBidCache(pos, { adjustedPrice, cVar, ad, nurl });
  }

  /**
   *
   * @param {string} priceCode - price bucket code
   * @returns {number} - price in CPM for the bucket
   */
  getBidFromBucketMap(priceCode: string | number): string {
    /* Override this method in child class if needed */
    return String(priceCode);
  }

  /**
   * - Adjust bid price (cpm)
   *
   * @param {string|number} price - Bid price to adjust
   * @returns {string|number} - Adjusted price
   * @access private
   */
  _bidPriceAdjust(price: string | number): string | number {
    const nprice = Number.parseFloat(String(price));
    if (this._bidPriceDiscount === 1 || Number.isNaN(nprice) || nprice <= 0) {
      return price;
    }
    return (nprice * this._bidPriceDiscount).toFixed(4);
  }

  /**
   * - getter for _enableReporting
   *
   * @returns {boolean} - _enableReporting
   * @access private
   */
  _doReporting(): boolean {
    return this._enableReporting;
  }

  /**
   * - get us_privacy string
   *
   * @returns {object} - us_privacy string
   */
  _getPrivacyString() {
    // eslint-disable-next-line camelcase
    return { us_privacy: getCCPA() };
  }
}
export default _bidbase;
